import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route } from 'react-router';
import { useMemo, type ReactNode } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Auth0ProviderWithRouter, RequireAuth, useAuthTokenWiring } from '@my/platform/auth';
import { ToastContainer } from '@my/ui';
import { ShieldCheck } from 'lucide-react';
import { RequireRole } from '@/auth/RequireRole';
import { getConfig } from '@/configuration/config';
import { AppShell } from '@/layout/AppShell';
import { HomePage } from '@/features/home/HomePage';
import { CheckListPage } from '@/features/checks/CheckListPage';
import { CheckDetailPage } from '@/features/checks/CheckDetailPage';
import { TemplateListPage } from '@/features/templates/TemplateListPage';
import { OrganisationPage } from '@/features/settings/organisation/OrganisationPage';
import { UsersAndAccessPage } from '@/features/settings/users-and-access/UsersAndAccessPage';
import { resolveBootLanguage } from '@/localisation';
import { Diagnostics } from '@/pages/Diagnostics';
import { NotFound } from '@/pages/NotFound';

/**
 * Bridges Auth0 → axios. Has to live INSIDE Auth0Provider so the hook can read
 * its context. Renders nothing.
 */
function AuthTokenBridge() {
  useAuthTokenWiring();
  return null;
}

export function App() {
  const cfg = getConfig();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 30_000,
          },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <MaybeMapsProvider apiKey={cfg.googleMapsApiKey}>
        <BrowserRouter>
          <Auth0ProviderWithRouter
            settings={{
              domain: cfg.a0Domain,
              clientId: cfg.a0ClientId,
              audience: cfg.a0Audience,
              subscription: 'safety',
              extraAuthorizationParams: { ui_locales: resolveBootLanguage() },
            }}
          >
            <AuthTokenBridge />
            <Routes>
              {/* Authenticated app shell — every screen renders inside <AppShell />. */}
              <Route
                element={
                  <RequireAuth brand={{ title: 'My Safety', icon: ShieldCheck }}>
                    <AppShell />
                  </RequireAuth>
                }
              >
                <Route index element={<HomePage />} />
                <Route path="dashboard" element={<HomePage />} />

                {/*
                  Walk-around checks — the core of the Safety app. List + detail
                  are placeholders for now; wire the real screens in as the
                  Safety API lands. Any authenticated Safety user can reach them.
                */}
                <Route path="checks" element={<CheckListPage />} />
                <Route path="checks/new" element={<CheckDetailPage />} />
                <Route path="checks/:id" element={<CheckDetailPage />} />
                <Route path="templates" element={<TemplateListPage />} />

                {/*
                  Settings tree — Owner / Admin only. RequireRole sits as a
                  layout route so all sub-routes share the guard without each
                  one repeating the wrapper. A Manager / User who deep-links
                  here gets bounced to /dashboard. Screens are placeholders
                  for now — wire real Settings pages in as Safety features land.
                */}
                <Route element={<RequireRole roles={['owner', 'admin']} />}>
                  <Route path="settings/organisation" element={<OrganisationPage />} />
                  <Route path="settings/users-and-access" element={<UsersAndAccessPage />} />
                </Route>

                <Route path="_diagnostics" element={<Diagnostics />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Auth0ProviderWithRouter>
        </BrowserRouter>
      </MaybeMapsProvider>
      <ToastContainer />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

/**
 * Wraps children in `<APIProvider>` only when an API key is configured. The
 * google-maps SDK lazily loads on the first `useMapsLibrary(...)` call inside;
 * a missing key just means map widgets render in a degraded state rather than
 * blowing up the whole app at boot.
 */
function MaybeMapsProvider({ apiKey, children }: { apiKey?: string; children: ReactNode }) {
  if (!apiKey) return <>{children}</>;
  return (
    <APIProvider apiKey={apiKey} libraries={['places']}>
      {children}
    </APIProvider>
  );
}
